import { aiVideoI2vPromptCaseAPI } from '@/utils/api';
import {
  DEFAULT_TITLE_PRESET_SCOPE,
  normalizeTitlePresetScope,
  resolveVisibleTitlePresetScopes,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import {
  VIDEO_DEFAULT_ASPECT_RATIO,
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_DEFAULT_GENERATION_MODE,
  VIDEO_DEFAULT_SEED,
  normalizeVideoAspectRatio,
  normalizeVideoDurationSec,
  normalizeVideoGenerationMode,
  normalizeVideoSeed,
} from '@/pages/ipe/workbench/constants/videoStudioConfig';
import {
  buildText2ImgCaseOwnerFilterOptions,
  resolveText2ImgCaseOwnerLabel,
} from '@/pages/ipe/workbench/utils/text2imgPromptCaseLoader';

export const VIDEO_I2V_CASE_DEFAULT_PAGE_SIZE = 20;
export const VIDEO_I2V_CASE_PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

/** 功能页空态试玩位：官方模板封面条最多条数 */
export const VIDEO_I2V_TRY_OFFICIAL_FETCH_SIZE = 20;

/** 将后端 CaseView 转为图生视频模板库统一结构（fallbackScope：后端未返回 caseScope 时的兜底） */
export function mapVideoI2vPromptCaseApiItem(item, fallbackScope = 'official') {
  if (!item || typeof item !== 'object') return null;
  const prompt = String(item.prompt || '').trim();
  const name = String(item.name || '').trim() || '未命名模板';
  const id = item.id != null ? String(item.id) : '';
  if (!id) return null;
  const status = item.status != null ? Number(item.status) : 1;
  if (status === 0) return null;
  return {
    id,
    name,
    prompt: prompt || name,
    generationMode: normalizeVideoGenerationMode(
      item.generationMode || VIDEO_DEFAULT_GENERATION_MODE,
    ),
    aspectRatio: normalizeVideoAspectRatio(item.aspectRatio || VIDEO_DEFAULT_ASPECT_RATIO),
    durationSec: normalizeVideoDurationSec(item.durationSec ?? VIDEO_DEFAULT_DURATION_SEC),
    seed: normalizeVideoSeed(item.seed ?? VIDEO_DEFAULT_SEED),
    coverUrl: String(item.coverUrl || item.url || '').trim(),
    scope: normalizeTitlePresetScope(item.caseScope || item.scope || fallbackScope),
    canEdit: Boolean(item.canEdit),
    canDelete: Boolean(item.canDelete),
    ownerUserId: item.ownerUserId ?? null,
    ownerDisplayName: String(item.ownerDisplayName || '').trim(),
    sortWeight: Number(item.sortWeight) || 0,
    source: 'api',
  };
}

/**
 * 按 scope 真分页拉取图生视频模板（个人 / 团队 / 官方）。
 * includeOfficial=true 时后端会把「官方」合并进本次查询（前端模板库锁定作用域时使用）。
 */
export async function loadVideoI2vPromptCasesPage({
  scope = DEFAULT_TITLE_PRESET_SCOPE,
  keyword = '',
  ownerUserId = '',
  page = 1,
  pageSize = VIDEO_I2V_CASE_DEFAULT_PAGE_SIZE,
  includeOfficial = false,
} = {}) {
  const safeScope = normalizeTitlePresetScope(scope);
  const ownerId = ownerUserId != null && String(ownerUserId).trim() !== ''
    ? String(ownerUserId).trim()
    : undefined;
  const res = await aiVideoI2vPromptCaseAPI.list({
    scope: safeScope,
    keyword: String(keyword || '').trim() || undefined,
    ownerUserId: safeScope === 'team' ? ownerId : undefined,
    page,
    pageSize,
    includeOfficial: includeOfficial ? true : undefined,
  });
  const items = (Array.isArray(res?.items) ? res.items : [])
    .map((raw) => mapVideoI2vPromptCaseApiItem(raw, safeScope))
    .filter(Boolean);
  return {
    items,
    total: Number(res?.total) || 0,
    page: Number(res?.page) || page || 1,
    pageSize: Number(res?.pageSize) || pageSize || VIDEO_I2V_CASE_DEFAULT_PAGE_SIZE,
    caseScope: res?.caseScope || safeScope,
  };
}

/**
 * 拉取官方模板中带封面的条目，供空态「或者用案例试试」展示（仅图片）。
 */
export async function loadVideoI2vOfficialTryCases(limit = 3) {
  const safeLimit = Math.max(1, Number(limit) || 3);
  const res = await loadVideoI2vPromptCasesPage({
    scope: 'official',
    page: 1,
    pageSize: Math.max(safeLimit, VIDEO_I2V_TRY_OFFICIAL_FETCH_SIZE),
  });
  return (Array.isArray(res.items) ? res.items : [])
    .filter((item) => String(item?.coverUrl || '').trim())
    .slice(0, safeLimit);
}

export const resolveVideoI2vCaseOwnerLabel = resolveText2ImgCaseOwnerLabel;
export const buildVideoI2vCaseOwnerFilterOptions = buildText2ImgCaseOwnerFilterOptions;

export { resolveVisibleTitlePresetScopes, DEFAULT_TITLE_PRESET_SCOPE };

/** 工作流执行：优先用 params.supplementPrompt / prompt，否则按模板 ID 拉取文本补充 */
export async function resolveVideoI2vWorkflowPrompt(params = {}) {
  let prompt = String(params.supplementPrompt || params.prompt || '').trim();
  if (prompt) return prompt;
  const caseId = String(params.videoI2vPromptCaseId || '').trim();
  if (!caseId) return '';
  try {
    const res = await aiVideoI2vPromptCaseAPI.getById(caseId);
    const mapped = mapVideoI2vPromptCaseApiItem(res?.case);
    return String(mapped?.prompt || '').trim();
  } catch {
    return '';
  }
}
